import React, { useEffect, useState } from 'react';
import { NpcPublicDTO } from '@hexcrawl/shared-utils';
import { npcService } from '../../services/npcService';
import Modal from '../common/Modal';
import { NpcDetail } from './NpcDetail';

interface Props {
  playerCharacterId: string;
  playerCharacterName: string;
  onClose: () => void;
}

export const NpcRoster: React.FC<Props> = ({ playerCharacterId, playerCharacterName, onClose }) => {
  const [npcs, setNpcs] = useState<NpcPublicDTO[]>([]);
  const [selectedNpc, setSelectedNpc] = useState<NpcPublicDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    npcService.listNpcs(playerCharacterId)
      .then(setNpcs)
      .catch((e: any) => setError(e?.message || 'Failed to load NPCs'))
      .finally(() => setIsLoading(false));
  }, [playerCharacterId]);

  if (selectedNpc) {
    return (
      <Modal isOpen={true} onClose={onClose}>
        <NpcDetail npc={selectedNpc} onBack={() => setSelectedNpc(null)} />
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} onClose={onClose}>
      <div style={{ width: 380, fontFamily: 'monospace' }}>
        <div style={{ fontSize: 16, fontWeight: 'bold', color: '#e8e8ff', marginBottom: 4 }}>
          NPCs — {playerCharacterName}
        </div>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 16 }}>
          Characters who have crossed paths with this adventurer
        </div>

        {isLoading && (
          <div style={{ color: '#888', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
            Loading…
          </div>
        )}

        {error && (
          <div style={{ color: '#f88', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {!isLoading && !error && npcs.length === 0 && (
          <div style={{ color: '#666', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
            No NPCs yet. They'll appear here after your first adventure.
          </div>
        )}

        {!isLoading && npcs.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '60vh', overflowY: 'auto' }}>
            {npcs.map(npc => (
              <NpcCard key={npc.id} npc={npc} onClick={() => setSelectedNpc(npc)} />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

const NpcCard: React.FC<{ npc: NpcPublicDTO; onClick: () => void }> = ({ npc, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: '#1a1a2e',
      border: '1px solid #334',
      borderRadius: 6,
      padding: '10px 12px',
      cursor: 'pointer',
      display: 'flex',
      gap: 10,
      alignItems: 'flex-start',
    }}
    onMouseEnter={e => (e.currentTarget.style.borderColor = '#556')}
    onMouseLeave={e => (e.currentTarget.style.borderColor = '#334')}
  >
    {npc.sprite && (
      <img
        src={`/sprites/${npc.sprite}`}
        alt=""
        style={{ width: 36, height: 36, imageRendering: 'pixelated', flexShrink: 0 }}
        onError={e => { e.currentTarget.style.display = 'none'; }}
      />
    )}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#e8e8ff', marginBottom: 3 }}>
        {npc.name}
        <span style={{ fontSize: 10, color: '#666', fontWeight: 'normal', marginLeft: 8 }}>
          Lv.{npc.level}
        </span>
      </div>
      {npc.playerExposedDescription && (
        <div style={{ fontSize: 11, color: '#aaa', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {npc.playerExposedDescription}
        </div>
      )}
      {!npc.playerExposedDescription && (
        <div style={{ fontSize: 11, color: '#555', fontStyle: 'italic' }}>
          A mysterious figure…
        </div>
      )}
    </div>
    <div style={{ fontSize: 16, color: '#444', alignSelf: 'center', flexShrink: 0 }}>›</div>
  </div>
);
